import { KEY_HINT_FLEX, shortcut } from "../../lib/platformKeys";

export type Pick = { label: string; value: string; description: string; suggested: boolean; pickId: string };

// 0051 S1b: whether the spec is handed off, read from the root's served copy
// ("unknown" is not "done"; the marked card's holds-up line shows only on
// "open").
export type HandOffState = "open" | "done" | "unknown";

// 0051 S1a: an option's text - its value, else its label, verbatim (never
// stripped or trimmed), exactly what selecting it writes as the draft - so
// QuestionPanel's draftIsPick reads a draft equal to it as that option.
export const pickText = (p: Pick) => p.value || p.label;

// ---- Key acts ----------------------------------------------------------

// 0051 S1c: the keydown lands in a text box (a textarea or an input), where a
// letter types and an arrow moves the caret, so neither acts nor navigates.
export function isTypingTarget(e: { target: EventTarget | null }): boolean {
  const tag = e.target instanceof HTMLElement ? e.target.tagName : "";
  return tag === "TEXTAREA" || tag === "INPUT";
}

// The keydowns the Review cards bind as ACTS: "submit" is Cmd/Ctrl+Enter
// wherever focus sits (Save & next, the fork's save, the reject reason, the
// covered-by amend); "confirm" and "reject" are y and n outside a text box
// (a decision's Confirm and Reject).
export type CardKeyAct = "submit" | "confirm" | "reject";

// 0051 S1c: the ONE spelling of a card's key acts. Every card reads it for its
// own key checks, and Review's keyboard guard (views/Review.tsx) reads it for
// the keys it holds. The arrows and Escape navigate; they are never acts.
// Takes a DOM keydown or React's synthetic one.
export function cardKeyAct(e: {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  target: EventTarget | null;
}): CardKeyAct | null {
  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") return "submit";
  if (isTypingTarget(e)) return null;
  if (e.key === "y" || e.key === "Y") return "confirm";
  if (e.key === "n" || e.key === "N") return "reject";
  return null;
}

// ---- Shared sub-components ------------------------------------------

export function NavFooter({
  pos,
  nothingWaits,
  onPrev,
  onNext,
}: {
  pos: number;
  // 0051 S1c: nothing waits on the founder in either queue (Review's
  // forward rule): only then does the forward button read "done".
  nothingWaits: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="mt-12 pt-6 border-t border-ink-800 flex items-center justify-between text-xs font-mono text-ink-400">
      <button
        type="button"
        onClick={onPrev}
        disabled={pos === 0}
        className="hover:text-ink-200 transition-colors disabled:cursor-not-allowed disabled:opacity-30"
      >
        ← previous
      </button>
      <div className={`${KEY_HINT_FLEX} items-center gap-3 text-ink-500`}>
        <span>{shortcut("←/→")} navigate</span>
        <span className="text-ink-700">·</span>
        <span>{shortcut("↵")} submit</span>
        <span className="text-ink-700">·</span>
        <span>esc back</span>
      </div>
      <button
        type="button"
        onClick={onNext}
        className="hover:text-ink-200 transition-colors"
      >
        {nothingWaits ? "done →" : "skip →"}
      </button>
    </div>
  );
}

// ---- Helpers ---------------------------------------------------------

// readPicks normalizes fields.parsed_picks (which round-trips through
// Firestore as `unknown`) into the typed Pick shape the Review cards render.
// Tolerates missing fields per pick (label fallback, suggested default
// false) so a partially-shaped pick doesn't crash the render.
export function readPicks(raw: unknown): Pick[] {
  if (!Array.isArray(raw)) return [];
  const out: Pick[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const label = asString(e.label);
    if (!label) continue;
    out.push({
      label,
      value: asString(e.value),
      description: asString(e.description),
      suggested: typeof e.suggested === "boolean" ? e.suggested : false,
      // 0131-T7: a pick may carry a structured routing id (fields.parsed_picks
      // entries stamped Go-side, e.g. the DELEGATE pick's pick_id="DELEGATE").
      // When present, clicking the pick sends it as selected_pick — the robust
      // route key, never a free-text match against the editable answer. Absent
      // (empty) for ordinary designed-fork picks, which route by answer text.
      pickId: asString(e.pick_id),
    });
  }
  return out;
}

export function asString(v: unknown): string {
  if (typeof v === "string") return v;
  return "";
}
